import store from './store.js';
import { v4 as uuidv4 } from 'uuid';
import findIndex from 'lodash/findIndex';

import ContentEncryptionService from './components/lib/ContentEncryptionService.js';
import Decrypter from './components/lib/Decrypter.js';
import Encrypter from './components/lib/Encrypter.js';
import { notesAPI } from './communication/notes';

export default {
    baseEndpoint: store.state.baseEndpoint,
    id: null,
    uuid: null,
    confirmed: false,
    isRendered: false,
    color: '',
    hasAnnotation: false,

    // PDF specific
    startPage: null,
    startPageStartOffset: null,
    startPageLength: null,
    endPage: null,
    endPageLength: null,

    // EPUB R1 legacy fields — used during lazy migration from R1 to R2.
    // Once all highlights have been migrated to R2 (epubR2Location has startCss),
    // these fields can be removed: epubLocationStartChar, epubLocationStartCfi,
    // epubLocationEndCfi, epubLocation.
    // Note: epubChapter is still used by R2 highlights for chapter identification.
    epubLocationStartChar: '',
    epubChapter: '',
    epubLocationStartCfi: '',
    epubLocationEndCfi: '',
    epubLocation: {},

    // @R2 EPUB R2 specific - CSS selector-based positioning
    location: null,
    epubR2Location: null,

    totalLength: null,
    truncated: true,
    copied: false,
    showCopyTooltip: false,
    totalText: null,
    annotationText: null,

    create() {
        this.uuid = uuidv4();
    },

    isStored() {
        return Object.prototype.hasOwnProperty.call(this, 'id') && this.id != null && this.id !== '';
    },

    /**
     * Determines if this highlight uses the legacy R1 format (offset-based)
     * vs the new R2 format (CSS-selector-based).
     * Legacy highlights have epubLocationStartChar but no R2 location data.
     */
    isLegacy() {
        return !this.location || !this.location?.selection;
    },

    canBeDeleted() {
        return this.isStored() || (this.isRendered && store.state.anonymousSession);
    },

    getUniqueCssClass() {
        return `uuid-${this.uuid}`;
    },

    getCssClassForMarker() {
        return `${this.getUniqueCssClass()} ${this.isStored() ? 'confirmed' : 'unconfirmed'}`;
    },

    updateAnnotationText() {
        if (!this.hasAnnotation) return;
        const index = findIndex(store.state.selections, ['uuid', this.uuid]);
        store.state.selections.splice(index, 1, { ...store.state.selections[index], ...this });
    },

    async reHydrate() {
        // @R2: Reconstruct R2 location from stored epubR2Location (DB → DOM rendering format)
        if (this.epubR2Location?.startCss) {
            // Full CSS selector-based R2 (new highlights created in R2)
            this.location = {
                selection: this.epubR2Location,
            };
        } else if (this.epubR2Location?.text) {
            // @R2 Text-based migration (legacy R1 → R2 via text search)
            this.location = {
                selection: { text: this.epubR2Location.text },
            };
        }

        this.totalText = await this.maybeDecryptTotalText();
        return this;
    },

    async store() {
        try {
            const encryptedText = await this.maybeEncryptTotalText();
            const response = await notesAPI.store({ ...this, totalText: encryptedText });
            this.confirmed = true;
            this.id = response.id;
            store.state.selections.push(this);
        } catch (e) {
            console.error('error', e);
        }
    },

    async update() {
        try {
            const response = await notesAPI.update(this.id, {
                color: this.color,
                annotationText: this.annotationText,
            });

            const index = findIndex(store.state.selections, ['uuid', this.uuid]);
            store.state.selections.splice(index, 1, {
                ...store.state.selections[index],
                ...response,
            });
        } catch (e) {
            console.error('error', e);
        }
    },

    /**
     * @R2 Lazy migration: persist R2 location data to DB after successful text-based rendering.
     * Called from readium2.js when a legacy R1 highlight renders successfully via text search.
     * This converts the highlight from R1 format to R2 text-based format in the database.
     */
    async persistR2Migration(epubR2Location) {
        // Skip if not stored in DB, or already migrated
        if (!this.isStored() || this.epubR2Location) return;

        try {
            await notesAPI.update(this.id, {
                color: this.color,
                annotationText: this.annotationText,
                position: {
                    type: 'epub',
                    epubR2Location,
                    epubChapter: this.epubChapter,
                },
            });
            this.epubR2Location = epubR2Location;
        } catch (e) {
            console.error('persistR2Migration failed', this.uuid?.substring(0, 8), e);
        }
    },

    async destroy() {
        try {
            await notesAPI.delete(this.id);
        } catch (e) {
            console.error('error', e);
        }
    },

    async maybeDecryptTotalText() {
        /**
         * If content encryption is enabled, try to decrypt the highlight raw text
         */
        if (ContentEncryptionService.shouldDecrypt()) {
            return await Decrypter.decryptText(this.totalText);
        }

        return this.totalText;
    },

    async maybeEncryptTotalText() {
        /**
         * If content encryption is enabled, try to decrypt the highlight raw text
         */
        if (ContentEncryptionService.shouldEncrypt()) {
            return await Encrypter.encryptText(this.totalText);
        }

        return this.totalText;
    },
};
